import { SystemConfigurationUpsert } from "../api/set-config-params";
import { ParamsProvider, useParamsContext } from "../contexts/params-context";
import { Button, CircularProgress, Stack, Typography, Switch, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

interface ParamsManagerProps {
  organizacionId?: string;
}

export default function ParamsManager({ organizacionId }: ParamsManagerProps) {
  return (
    <ParamsProvider organizacionId={organizacionId}>
      <ParamsEditor />
    </ParamsProvider>
  );
}

function ParamsEditor() {
  const { params, loading, error, save } = useParamsContext();
  const [local, setLocal] = useState(params);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(params);
  }, [params]);

  const upserts: SystemConfigurationUpsert[] = useMemo(
    () => local.map((e) => ({ key: e.key, type: e.type, stringValue: e.stringValue })),
    [local]
  );

  if (loading) return <Typography>Cargando parámetros...</Typography>;
  if (error) return <Typography color="error">Error cargando parámetros</Typography>;

  return (
    <Stack gap={2} padding={3}>
      <Typography variant="h6">Parámetros del Sistema</Typography>
      <Stack sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
        {local.map((e, idx) => (
          <Stack gap={1} p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }} key={e.key}>
            <Typography variant="subtitle2" fontWeight="bold">
              {e.key}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {e.description}
            </Typography>
            {e.type === "Boolean" ? (
              <Stack direction="row" gap={1} alignItems="center">
                <Typography>Activo</Typography>
                <Switch
                  checked={e.stringValue.toLowerCase() === "true"}
                  onChange={(_, checked) => {
                    const next = [...local];
                    next[idx] = { ...next[idx], stringValue: checked ? "true" : "false" };
                    setLocal(next);
                  }}
                />
              </Stack>
            ) : e.type === "Number" ? (
              <TextField
                type="number"
                label="Valor"
                size="small"
                value={Number(e.stringValue)}
                onChange={(ev) => {
                  const val = ev.target.value;
                  const next = [...local];
                  next[idx] = { ...next[idx], stringValue: val === "" ? "0" : val };
                  setLocal(next);
                }}
                sx={{ maxWidth: 200 }}
              />
            ) : (
              <TextField
                label="Valor"
                size="small"
                value={e.stringValue}
                onChange={(ev) => {
                  const next = [...local];
                  next[idx] = { ...next[idx], stringValue: ev.target.value ?? "" };
                  setLocal(next);
                }}
                sx={{ maxWidth: 300 }}
              />
            )}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={async () => {
            setSaving(true);
            try { await save(upserts); } finally { setSaving(false); }
          }}
        >
          Guardar
        </Button>
      </Stack>
    </Stack >
  );
}
